import { Zap, Shield, Heart, Eye } from "lucide-react";
import { ABOUT_IMG } from "../lib/constants";
import { CallButton, QuoteButton, SectionLabel } from "../components/Shared";

const VALUES = [
  {
    icon: Zap,
    t: "Speed",
    d: "We act quickly because every minute matters when you are stuck.",
  },
  {
    icon: Shield,
    t: "Safety",
    d: "Your safety and your vehicle's protection come first.",
  },
  {
    icon: Heart,
    t: "Care",
    d: "We treat every customer with patience and respect.",
  },
  {
    icon: Eye,
    t: "Clarity",
    d: "No confusion. No vague answers. Just clear next steps.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <SectionLabel>About ExpressBreakdown</SectionLabel>
              <h1
                data-testid="about-headline"
                className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-display font-extrabold tracking-tighter leading-[0.95]"
              >
                Reliable recovery.
                <br />
                Calm service.
                <br />
                <span className="text-brand">Fast action.</span>
              </h1>
            </div>
            <div className="lg:col-span-5 space-y-6 text-base sm:text-lg text-white/70 leading-relaxed">
              <p>
                ExpressBreakdown was built for drivers who need help without
                confusion, delays or pushy sales. Whether your vehicle will
                not start, you have suffered tyre damage, or you need safe
                transport to a garage, we provide a professional recovery
                service focused on speed, care and communication.
              </p>
              <p>
                We understand that breakdowns often happen at the worst
                possible time. That is why our service is simple: call us,
                explain the problem, and we help you get the right recovery
                support quickly.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row gap-4">
                <CallButton testId="about-call-btn" label="Call Now" />
                <QuoteButton testId="about-quote-btn" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMAGE SHOWCASE */}
      <section className="border-b border-white/10">
        <div className="relative h-[60vh] min-h-[420px]">
          <img
            src={ABOUT_IMG}
            alt="ExpressBreakdown recovery truck"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
              <div className="inline-flex items-center gap-3 bg-brand text-black px-4 py-2 text-xs font-bold uppercase tracking-[0.22em]">
                On the road · UK
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-b border-white/10 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <SectionLabel>Our values</SectionLabel>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight">
              What we stand by, every call.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {VALUES.map((v, i) => (
              <div
                key={v.t}
                data-testid={`value-card-${i}`}
                className="bg-[#0f0f0f] p-10"
              >
                <div className="h-12 w-12 flex items-center justify-center border border-brand text-brand">
                  <v.icon className="h-5 w-5" strokeWidth={2.25} />
                </div>
                <h3 className="mt-6 font-display font-extrabold text-2xl">
                  {v.t}
                </h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">
                  {v.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-brand text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold tracking-tight max-w-2xl">
            Stuck? We'll get you moving again.
          </h2>
          <a
            href="tel:+447717759310"
            data-testid="about-cta-call"
            className="inline-flex items-center justify-center gap-3 bg-black text-brand font-bold uppercase tracking-wider text-base px-7 py-5"
          >
            Call Now
          </a>
        </div>
      </section>
    </div>
  );
}
